import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import FrameList from './FrameList for PrintandFramewithMatboardandGlass';
import FrameStickSpecPanel from './FrameStickSpecPanel';
import Swal from 'sweetalert2';
import { totalPrice as calculatePrice } from './PricingEngine';

function FrameYourCanvasArt({ image, originalImage }) {
    const [userImage, setUserImage] = useState(() => {
        if (image) return image;
        return localStorage.getItem('opticon_last_image') || null;
    });
    const [uploadedUserImage, setUploadedUserImage] = useState(() => originalImage || userImage);
    const fileInputRef = useRef(null);

    const [width, setWidth] = useState(30);
    const [height, setHeight] = useState(30);
    const [imageAspect, setImageAspect] = useState(null);
    const [sizeMode, setSizeMode] = useState('manual');
    const [imagePxSize, setImagePxSize] = useState(null);

    const PRINT_DPI = 300;
    const pxToCm = (px) => ((px / PRINT_DPI) * 2.54).toFixed(1);

    const [frameOpen, setFrameOpen] = useState(false);
    const [selectedFrame, setSelectedFrame] = useState(null);

    const canvasRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [, setProgress] = useState(0);
    const [sizeTouched, setSizeTouched] = useState(false);
    const [shipArt, setShipArt] = useState('no');

    const [stretchMode, setStretchMode] = useState('no');
    const [stretchType, setStretchType] = useState('no-stretch');
    const [imgPos, setImgPos] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    const frameOptions = FrameList;
    const CM_TO_MM = 10;

    const handleImageUpload = (e) => {
        if (!e.target.files?.length) return;

        const file = e.target.files[0];
        if (!file.type.startsWith('image/')) return;

        const MAX_MB = 64;
        if (file.size > MAX_MB * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: 'Image too large',
                text: 'Please upload an image under 64MB.',
                confirmButtonText: 'OK'
            });
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            const img = new Image();

            img.onload = () => {
                const imgW = img.naturalWidth;
                const imgH = img.naturalHeight;

                setImagePxSize({ w: imgW, h: imgH });

                const aspect = imgW / imgH;
                const defaultW = 30;
                const defaultH = Math.round(defaultW / aspect);

                setWidth(defaultW);
                setHeight(defaultH);
                setSizeTouched(false);

                setUserImage(reader.result);
                setUploadedUserImage(reader.result);
                localStorage.setItem('opticon_last_image', reader.result);
            };

            img.src = reader.result;
        };

        reader.readAsDataURL(file);
    };
    // Stretch pricing (added to engine price)
    const STRETCH_PRICES = {
        'no-stretch': 0,
        'small': 7.95,
        'medium': 9.45,
        'large': 10.75
    };

    const getStretchValue = () => {
        switch (stretchType) {
            case 'small': return 1.02;
            case 'medium': return 1.05;
            case 'large': return 1.08;
            default: return 1;
        }
    };

    const getEngineStretchType = () => {
        switch (stretchType) {
            case 'small': return 'regular';
            case 'medium': return 'regular';
            case 'large': return 'gallery';
            default: return undefined;
        }
    };

    const pricing = calculatePrice({
        qty: 1,
        opening_width: width * CM_TO_MM,
        opening_height: height * CM_TO_MM,
        print_type: 'canvas',
        stretch_type: getEngineStretchType(),
        frame: selectedFrame || {},
        mat: {},
        glass: {},
        backing: {},
        extras: [],
        openings: 1,
        discount: 0
    });

    const stretchPrice = STRETCH_PRICES[stretchType] || 0;
    const totalPriceValue = pricing.finalPrice + stretchPrice;
    const GST_RATE = 0.1;
    const gstAmount = totalPriceValue * GST_RATE;
    const totalWithGST = totalPriceValue + gstAmount;


    const updateCanvasSize = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const PREVIEW_PPCM = 20;
        canvas.width = Math.round(width * PREVIEW_PPCM);
        canvas.height = Math.round(height * PREVIEW_PPCM);
    };

    const handleMouseDown = (e) => {
        isDragging.current = true;
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;

        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;

        setImgPos((prev) => {
            let newX = prev.x + dx;
            let newY = prev.y + dy;

            const canvas = canvasRef.current;
            const W = canvas.width;
            const H = canvas.height;

            const stretchValue = getStretchValue();
            const limitX = (W * (stretchValue - 1)) / 2;
            const limitY = (H * (stretchValue - 1)) / 2;

            newX = Math.max(-limitX, Math.min(limitX, newX));
            newY = Math.max(-limitY, Math.min(limitY, newY));

            return { x: newX, y: newY };
        });

        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    const renderCanvas = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;

        ctx.clearRect(0, 0, W, H);

        const PREVIEW_PX_PER_CM = 10;
        const frameTpx =
            ((selectedFrame?.thickness || 0) / 10) * PREVIEW_PX_PER_CM;

        const maxInset = Math.min(W, H) / 2 - 4;
        const insetPx = Math.min(frameTpx, maxInset);

        /* ========================================================
           WHITE CANVAS BACKGROUND
           Show white canvas when there is no uploaded image
        ======================================================== */

        const baseW = W - insetPx * 2;
        const baseH = H - insetPx * 2;

        if (baseW > 0 && baseH > 0) {
            ctx.save();

            ctx.fillStyle = '#ffffff';

            ctx.fillRect(
                insetPx,
                insetPx,
                baseW,
                baseH
            );

            ctx.restore();
        }

        /* ========================================================
           IMAGE
        ======================================================== */

        if (userImage) {
            const img = new Image();
            img.src = userImage;

            await new Promise((resolve) => {
                img.onload = resolve;
            });

            let drawW = baseW;
            let drawH = baseH;
            let offsetX = insetPx;
            let offsetY = insetPx;

            const stretchValue = getStretchValue();

            if (stretchValue !== 1) {
                drawW = baseW * stretchValue;
                drawH = baseH * stretchValue;

                offsetX =
                    insetPx -
                    (drawW - baseW) / 2;

                offsetY =
                    insetPx -
                    (drawH - baseH) / 2;
            }

            ctx.save();

            ctx.beginPath();

            ctx.rect(
                insetPx,
                insetPx,
                baseW,
                baseH
            );

            ctx.clip();

            ctx.drawImage(
                img,
                offsetX + imgPos.x,
                offsetY + imgPos.y,
                drawW,
                drawH
            );

            ctx.restore();
        }

        /* ========================================================
           FRAME WITH MITERED CORNERS
           Using the reference frame rendering logic
        ======================================================== */

        if (selectedFrame?.img && frameTpx > 0) {
            const frameImg = new Image();
            frameImg.src = selectedFrame.img;

            await new Promise((resolve) => {
                frameImg.onload = resolve;
            });

            const miter = frameTpx;

            /* ====================================================
               TOP FRAME
            ==================================================== */

            ctx.save();

            ctx.beginPath();

            ctx.moveTo(0, 0);
            ctx.lineTo(W, 0);
            ctx.lineTo(W - miter, frameTpx);
            ctx.lineTo(miter, frameTpx);

            ctx.closePath();
            ctx.clip();

            ctx.drawImage(
                frameImg,
                0,
                0,
                W,
                frameTpx
            );

            ctx.restore();

            /* ====================================================
               BOTTOM FRAME
            ==================================================== */

            ctx.save();

            ctx.beginPath();

            ctx.moveTo(miter, H - frameTpx);
            ctx.lineTo(W - miter, H - frameTpx);
            ctx.lineTo(W, H);
            ctx.lineTo(0, H);

            ctx.closePath();
            ctx.clip();

            ctx.translate(W, H);
            ctx.rotate(Math.PI);

            ctx.drawImage(
                frameImg,
                0,
                0,
                W,
                frameTpx
            );

            ctx.restore();

            /* ====================================================
               LEFT FRAME
            ==================================================== */

            ctx.save();

            ctx.beginPath();

            ctx.moveTo(0, 0);
            ctx.lineTo(frameTpx, miter);
            ctx.lineTo(frameTpx, H - miter);
            ctx.lineTo(0, H);

            ctx.closePath();
            ctx.clip();

            ctx.translate(0, H);
            ctx.rotate(-Math.PI / 2);

            ctx.drawImage(
                frameImg,
                0,
                0,
                H,
                frameTpx
            );

            ctx.restore();

            /* ====================================================
               RIGHT FRAME
            ==================================================== */

            ctx.save();

            ctx.beginPath();

            ctx.moveTo(W - frameTpx, miter);
            ctx.lineTo(W, 0);
            ctx.lineTo(W, H);
            ctx.lineTo(W - frameTpx, H - miter);

            ctx.closePath();
            ctx.clip();

            ctx.translate(W, 0);
            ctx.rotate(Math.PI / 2);

            ctx.drawImage(
                frameImg,
                0,
                0,
                H,
                frameTpx
            );

            ctx.restore();
        }
    };

    const handleAddToCart = async () => {
        if (!canvasRef.current) return;

        setLoading(true);
        setProgress(0);

        try {
            let imageToSend = '';

            if (stretchType !== 'no-stretch') {
                imageToSend = canvasRef.current.toDataURL('image/png');
            } else if (userImage) {
                imageToSend = userImage;
            } else {
                imageToSend = canvasRef.current.toDataURL('image/png');
            }

            const payload = {
                id: 1265,
                quantity: 1,
                meta_data: [
                    { key: 'image_size', value: `${width}x${height}` },
                    { key: 'frame_name', value: selectedFrame?.name || 'No Frame' },
                    { key: 'frame_price', value: totalPriceValue },
                    { key: 'base_price', value: pricing.basePrice },
                    { key: 'total_price', value: totalWithGST },
                    { key: 'ship_your_art', value: shipArt },
                    { key: 'print_type', value: 'canvas' },
                    { key: 'stretch_value', value: stretchType },
                    { key: 'edited_image', value: imageToSend },
                    { key: 'user-uploaded-image', value: uploadedUserImage }
                ]
            };
            console.log('Payload for cart:', payload);
            await axios.post(
                'https://veyraprints.infinite-innovate.com/wp-json/frameandcanvas/v1/frame-your-canvas-art/add-to-cart',
                payload,
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    onUploadProgress: (e) => {
                        if (!e.total) return;
                        setProgress(Math.round((e.loaded * 100) / e.total));
                    }
                }
            );

            setProgress(100);

            setTimeout(() => {
                window.location.href = 'https://veyraprints.infinite-innovate.com/cart';
            }, 700);
        } catch (err) {
            console.error(err);
            alert('Add to cart failed');
            setLoading(false);
            setProgress(0);
        }
    };

    const handleRemoveImage = () => {
        setUserImage(null);
        setImageAspect(null);
        setImagePxSize(null);
        setSizeMode('manual');
        setWidth(30);
        setHeight(30);
        setSizeTouched(false);
        localStorage.removeItem('opticon_last_image');
    };

    useEffect(() => {
        (async () => {
            updateCanvasSize();
            await renderCanvas();
        })();
    }, [userImage, selectedFrame, width, height, stretchType, imgPos]);

    useEffect(() => {
        if (!selectedFrame && frameOptions.length > 0) {
            setSelectedFrame(frameOptions[0]);
        }
    }, [selectedFrame, frameOptions]);

    useEffect(() => {
        if (!userImage || imagePxSize) return;

        const img = new Image();
        img.onload = () => {
            setImageAspect(img.naturalWidth / img.naturalHeight);
            setImagePxSize({
                w: img.naturalWidth,
                h: img.naturalHeight
            });
            setSizeMode('image');
        };

        img.src = userImage;
    }, [userImage, imagePxSize]);

    useEffect(() => {
        if (image) {
            setUserImage(image);
            localStorage.setItem('opticon_last_image', image);
        }
    }, [image]);

    return (
        <div className="product-page">
            {loading && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                >
                    <div
                        style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            border: '5px solid rgba(255,255,255,0.1)',
                            borderTop: '5px solid #1E5FFF',
                            animation: 'spin 1s linear infinite',
                            marginBottom: '20px'
                        }}
                    ></div>

                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                        Loading...
                    </p>

                    <style>{`
                          @keyframes spin {
                              0% { transform: rotate(0deg); }
                              100% { transform: rotate(360deg); }
                          }
                      `}</style>
                </div>
            )}

            <div className="product-image-panel">
                <canvas
                    ref={canvasRef}
                    className="real-canvas-preview"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                />
            </div>

            <div className="product-info-panel">
                <h1 className="product-title">Frame Your Canvas Art</h1>

                <div className="print-option-row">
                    <label className="form-label">Canvas Size (cm)</label>
                    <div className="image-size-row">
                        <input
                            type="number"
                            value={width}
                            onChange={(e) => {
                                setWidth(Number(e.target.value));
                                setSizeTouched(true);
                                setSizeMode('manual');
                            }}
                            className="form-select"
                        />

                        <input
                            type="number"
                            value={height}
                            onChange={(e) => {
                                setHeight(Number(e.target.value));
                                setSizeTouched(true);
                                setSizeMode('manual');
                            }}
                            className="form-select"
                        />
                    </div>
                </div>

                <div className="print-option-row">
                    <label className="form-label">Upload Digital Canvas Image</label>

                    <div className="image-size-row">
                        <button
                            type="button"
                            className="btn-secondary image-upload-btn"
                            onClick={() => fileInputRef.current.click()}
                        >
                            {userImage ? 'Change Image' : 'Add Image'}
                        </button>

                        {userImage && (
                            <button
                                className="btn-danger image-remove-btn"
                                onClick={handleRemoveImage}
                                type="button"
                            >
                                Remove Image
                            </button>
                        )}

                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*"
                            hidden
                            onChange={handleImageUpload}
                        />
                    </div>
                </div>

                <div className="print-option-row">
                    <label className="form-label">Ship your art to us</label>

                    <select
                        className="form-select"
                        value={shipArt}
                        onChange={(e) => setShipArt(e.target.value)}
                    >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                    </select>
                </div>

                <div className="print-option-row frame-row">
                    <label className="form-label">Frame</label>
                    <div className="frame-dropdown-display" onClick={() => setFrameOpen(true)}>
                        <span>{selectedFrame?.name || 'Select Frame'}</span>
                        <span className="frame-arrow"></span>
                    </div>
                </div>

                <div className="price-cart-row">
                    <div className="product-price">
                        ${totalPriceValue.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)} = ${totalWithGST.toFixed(2)}
                    </div>

                    <button
                        className="add-to-cart-btn"
                        onClick={handleAddToCart}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Add to Cart'}
                    </button>
                </div>

                <FrameStickSpecPanel frame={selectedFrame} />
            </div>

            {frameOpen && (
                <div className="frame-sidebar open">
                    <div className="frame-sidebar-header">
                        <h3>Frames</h3>
                        <button onClick={() => setFrameOpen(false)}>X</button>
                    </div>

                    {frameOptions.map((frame) => (
                        <div
                            key={frame.id}
                            className={`frame-item ${selectedFrame?.id === frame.id ? 'active' : ''}`}
                            onClick={() => {
                                setSelectedFrame(frame);
                                setFrameOpen(false);
                            }}
                        >
                            <img src={frame.img} alt={frame.name} />
                            <div className="frame-meta">
                                <div className="frame-name">{frame.name}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default FrameYourCanvasArt;